import {Injectable, inject} from '@angular/core'
import type {AnalyticsProvider} from './analytics-provider.interface'
import {FirebaseAnalytics} from '@capacitor-firebase/analytics'
import {environment} from '@cart/src/environments/environment'
import {InfoService} from '../info.service'
import {LogType, Logger} from '../utils/logger'

@Injectable({providedIn: 'root'})
export class FirebaseAnalyticsProvider implements AnalyticsProvider {
  readonly #infoService = inject(InfoService)
  #initialized = false

  async init(): Promise<void> {
    if (!this.isEnabled()) {
      return
    }

    await FirebaseAnalytics.setEnabled({enabled: true})
    this.#initialized = true
  }

  isEnabled(): boolean {
    return environment.analytics && !this.#infoService.isWeb()
  }

  trackEvent(name: string, properties?: Record<string, unknown>): void {
    if (!this.isEnabled()) {
      return
    }

    Logger.log(LogType.debug, '[FirebaseAnalytics] trackEvent', name, properties)
    FirebaseAnalytics.logEvent({name, params: properties}).catch((error: unknown) => console.error(error))
  }

  identifyUser(userId: string, properties?: Record<string, unknown>): void {
    if (!this.isEnabled()) {
      return
    }

    Logger.log(LogType.debug, '[FirebaseAnalytics] identifyUser', userId, properties)
    FirebaseAnalytics.setUserId({userId}).then(() => {
      if (properties) {
        Object.entries(properties).forEach(([key, value]) => {
          FirebaseAnalytics.setUserProperty({key, value: String(value)}).catch((error: unknown) => console.error(error))
        })
      }
    })
  }

  resetUser(): void {
    if (!this.isEnabled()) {
      return
    }

    Logger.log(LogType.debug, '[FirebaseAnalytics] resetUser')
    FirebaseAnalytics.setUserId({userId: null}).catch((error: unknown) => console.error(error))
  }

  setScreen(name: string): void {
    if (!this.isEnabled() || !this.#initialized) {
      return
    }

    Logger.log(LogType.debug, '[FirebaseAnalytics] setScreen', name)
    FirebaseAnalytics.setCurrentScreen({screenName: name, screenClassOverride: name}).catch((error: unknown) =>
      console.error(error)
    )
  }
}
